import moment from 'moment';

/**
 * Canonical model/wire shape for a CALENDAR DATE (birth dates, expiry dates, start/end dates):
 * an offset-less `YYYY-MM-DDT00:00:00`.
 *
 * A calendar date is not an instant. The moment you express one as an instant — `toISOString()`,
 * a `Z` suffix, a `+01:00` offset — its day becomes a function of whichever timezone happens to
 * read it, and the two ends of this app do not agree on one:
 *
 *   - `parseISO('1974-02-22')` / `moment('1974-02-22')` / `new Date(1974, 1, 22)` all produce
 *     LOCAL midnight. In Europe/Prague that is `1974-02-22T00:00:00+01:00`, whose `toISOString()`
 *     is `'1974-02-21T23:00:00.000Z'` — the day before.
 *   - The backend's `CustomDateOnlyConverter` then does `.ToLocalTime().ToString("yyyy-MM-dd")`,
 *     re-projecting that instant into the SERVER's timezone. The day survives only when the
 *     server's offset is at least the browser's, so the same payload stores a different day on a
 *     UTC host than on a Europe/Prague host.
 *
 * An offset-less timestamp sidesteps both hops: it is read as local by everything in the stack
 * (`ejs-datepicker`, `parseISO`, `new Date`, `moment`, and Newtonsoft — which yields
 * `DateTimeKind.Unspecified`), so the calendar day stays literal.
 *
 * The time part is not decoration. Syncfusion's date picker rejects a bare `YYYY-MM-DD`:
 * `checkInvalidValue()` treats any digits-and-hyphens-only string as unparseable and clears its
 * own value without touching the form control, leaving a field that looks broken. Widening the
 * day to local midnight avoids that.
 *
 * This is the same convention documented and unit-tested at
 * `libs/formly/src/lib/hints/hint.utils.ts`, which now delegates here.
 */
export const DATE_ONLY_TIME_SUFFIX = 'T00:00:00';

/**
 * Formats accepted on the way in. `ISO_8601` covers the shapes we produce ourselves plus the
 * legacy `Z` instants already sitting in the database; the rest are Helios/import spellings.
 */
const PARSE_FORMATS: (string | typeof moment.ISO_8601)[] = [
  moment.ISO_8601,
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ss',
  'MM/DD/YYYY HH:mm:ss',
  'MM/DD/YYYY',
  'DD.MM.YYYY',
];

/**
 * Normalises a calendar date to the canonical offset-less `YYYY-MM-DDT00:00:00`.
 *
 * The day is always read from the value's LOCAL calendar fields, which is what makes this
 * timezone-stable: a `Date` at local midnight keeps its day, and a legacy `Z` instant
 * (`'1974-02-21T23:00:00.000Z'`) resolves back to the day it was written as — so reading a
 * drifted record through this helper repairs it rather than propagating the drift.
 *
 * @param value a `Date` (as produced by the picker or by the birth-number decoder), an
 *   already-canonical string, a legacy instant, or a Helios-formatted date.
 * @returns the canonical string; `undefined` for an empty value or an invalid `Date`; the trimmed
 *   input unchanged when it is a string we cannot parse — dropping data is worse than passing an
 *   unrecognised spelling through to the backend.
 */
export function toDateOnly(
  value: Date | string | null | undefined
): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (value instanceof Date) {
    const parsed = moment(value);
    return parsed.isValid()
      ? parsed.format('YYYY-MM-DD') + DATE_ONLY_TIME_SUFFIX
      : undefined;
  }

  const raw = String(value).trim();
  if (!raw) {
    return undefined;
  }

  const parsed = moment(raw, PARSE_FORMATS);
  return parsed.isValid()
    ? parsed.format('YYYY-MM-DD') + DATE_ONLY_TIME_SUFFIX
    : raw;
}

/**
 * Convenience wrapper for the `{ rodneCislo, datumNarozeni, ceskeRodneCislo }` group, which is the
 * shape the birth date travels in through the employee-card map functions.
 */
export function normalizeBirthDateGroup<
  T extends { datumNarozeni?: unknown } | null | undefined
>(group: T): T {
  if (!group) {
    return group;
  }
  return {
    ...group,
    datumNarozeni: toDateOnly(group.datumNarozeni as Date | string | null),
  };
}
